import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Autocomplete,
  CircularProgress,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel,
  List,
  ListItem,
  Divider,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { PrimaryButton, SecondaryButton } from '../../../../../components/atoms/Button';
import {
  purchaseDialogActionsSx,
  purchaseDialogContentSx,
  purchaseDialogInnerSx,
  purchaseDialogPaperProps,
  purchaseDialogTitleSx,
  purchaseFieldSx,
} from '../../../purchaseUiStyles';

interface User {
  _id: string;
  userName: string;
  phoneNumber: string;
  countryCode: string;
  email?: string;
  type: string;
}

interface Product {
  _id: string;
  name: string;
  brand: string;
  model: string;
  acType: string;
  tonnage: number;
  customerPrice: number;
}

interface CreateLeadDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (leadData: {
    userId: string;
    productId: string;
    remarks?: string;
  }) => void;
  users: User[];
  products: Product[];
  loading: boolean;
  productsLoading: boolean;
  onUserSearch: (search: string) => void;
  onCreateUser: (userData: any) => void;
}

const CreateLeadDialog: React.FC<CreateLeadDialogProps> = ({
  open,
  onClose,
  onSubmit,
  users,
  products,
  loading,
  productsLoading,
  onUserSearch,
  onCreateUser,
}) => {
  const [userTab, setUserTab] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState<{
    productId: string;
    quantity: number;
    price: number;
    name: string;
  } | null>(null);
  const [leadForm, setLeadForm] = useState({
    userId: '',
    productId: '',
    remarks: '',
  });
  const [newUserForm, setNewUserForm] = useState({
    userName: '',
    phoneNumber: '',
    countryCode: '+91',
    type: 'HNI',
    email: '',
  });

  const handleProductSelect = (productId: string) => {
    const product = products.find((p) => p._id === productId);
    if (!product) return;

    setSelectedProduct({
      productId,
      quantity: 1,
      price: product.customerPrice,
      name: product.name,
    });
    setLeadForm({ ...leadForm, productId });
  };

  const handleCreateUser = () => {
    onCreateUser(newUserForm);
  };

  const handleSubmit = () => {
    onSubmit(leadForm);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={purchaseDialogPaperProps}
    >
      <Box sx={purchaseDialogInnerSx}>
        <DialogTitle sx={purchaseDialogTitleSx}>
          Create Purchase Lead
          <IconButton onClick={onClose} size="small" sx={{ color: '#B0BEC5' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ ...purchaseDialogContentSx, maxHeight: '70vh', overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Tabs
                value={userTab}
                onChange={(_, newValue) => setUserTab(newValue)}
                sx={{
                  '& .MuiTabs-indicator': { backgroundColor: '#E31E25' },
                  '& .MuiTab-root': {
                    color: '#8A8D91',
                    '&.Mui-selected': { color: '#fff' },
                  },
                }}
              >
                <Tab label="Existing User" />
                <Tab label="New User" icon={<PersonAddIcon />} iconPosition="start" />
              </Tabs>

              {userTab === 0 ? (
                <Box sx={{ mt: 2 }}>
                  <Typography gutterBottom sx={{ color: '#fff', fontWeight: 600 }}>
                    Search & Select User
                  </Typography>
                  <Autocomplete
                    options={users}
                    loading={loading}
                    getOptionLabel={(user) =>
                      `${user.userName} - ${user.countryCode}${user.phoneNumber}${user.email ? ` (${user.email})` : ''}`
                    }
                    onInputChange={(_, newValue) => onUserSearch(newValue)}
                    onChange={(_, newValue) =>
                      setLeadForm({ ...leadForm, userId: newValue?._id || '' })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search user by name, phone or email"
                        sx={purchaseFieldSx}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <Typography gutterBottom sx={{ color: '#fff', fontWeight: 600 }}>
                    Add New User
                  </Typography>

                  <TextField
                    fullWidth
                    label="Full Name *"
                    value={newUserForm.userName}
                    onChange={(e) =>
                      setNewUserForm({ ...newUserForm, userName: e.target.value })
                    }
                    sx={{ mb: 2, ...purchaseFieldSx }}
                  />

                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <FormControl sx={{ width: '120px' }}>
                      <Select
                        value={newUserForm.countryCode}
                        onChange={(e) =>
                          setNewUserForm({
                            ...newUserForm,
                            countryCode: e.target.value,
                          })
                        }
                        sx={purchaseFieldSx}
                      >
                        <MenuItem value="+91">+91 (India)</MenuItem>
                        <MenuItem value="+1">+1 (USA)</MenuItem>
                        <MenuItem value="+44">+44 (UK)</MenuItem>
                        <MenuItem value="+971">+971 (UAE)</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Phone Number *"
                      value={newUserForm.phoneNumber}
                      onChange={(e) =>
                        setNewUserForm({
                          ...newUserForm,
                          phoneNumber: e.target.value,
                        })
                      }
                      sx={purchaseFieldSx}
                    />
                  </Box>

                  <TextField
                    fullWidth
                    label="Email (Optional)"
                    value={newUserForm.email}
                    onChange={(e) =>
                      setNewUserForm({ ...newUserForm, email: e.target.value })
                    }
                    sx={{ mb: 2, ...purchaseFieldSx }}
                  />

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <Select
                      value={newUserForm.type}
                      onChange={(e) =>
                        setNewUserForm({ ...newUserForm, type: e.target.value })
                      }
                      sx={purchaseFieldSx}
                    >
                      <MenuItem value="HNI">HNI</MenuItem>
                      <MenuItem value="AFFLUENT">Affluent</MenuItem>
                      <MenuItem value="MIDDLE_CLASS">Middle Class</MenuItem>
                      <MenuItem value="BUDGET">Budget</MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={handleCreateUser}
                    disabled={
                      loading || !newUserForm.userName || !newUserForm.phoneNumber
                    }
                    sx={{
                      backgroundColor: '#E31E25',
                      '&:hover': { backgroundColor: '#c41920' },
                    }}
                  >
                    {loading ? 'Creating...' : 'Create User'}
                  </Button>
                </Box>
              )}
            </Box>

            <Box>
              <Typography gutterBottom sx={{ color: '#fff', fontWeight: 600 }}>
                Select Product
              </Typography>

              <Box
                sx={{
                  border: '1px solid #333',
                  borderRadius: '8px',
                  p: 2,
                  mb: 2,
                  maxHeight: '300px',
                  overflow: 'auto',
                  backgroundColor: '#222',
                }}
              >
                {productsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress sx={{ color: '#E31E25' }} />
                  </Box>
                ) : products.length > 0 ? (
                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <RadioGroup
                      value={selectedProduct?.productId || ''}
                      onChange={(e) => handleProductSelect(e.target.value)}
                    >
                      <List dense>
                        {products.map((product) => (
                          <ListItem
                            key={product._id}
                            sx={{
                              borderBottom: '1px solid #333',
                              '&:last-child': { borderBottom: 'none' },
                            }}
                          >
                            <FormControlLabel
                              value={product._id}
                              control={
                                <Radio sx={{ color: '#E31E25', '&.Mui-checked': { color: '#E31E25' } }} />
                              }
                              label={
                                <Box sx={{ ml: 1 }}>
                                  <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                                    {product.name}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#8A8D91' }}>
                                    {product.brand} • {product.acType} • {product.tonnage} Ton
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#E31E25', fontWeight: 600 }}
                                  >
                                    ₹{product.customerPrice.toLocaleString()}
                                  </Typography>
                                </Box>
                              }
                              sx={{ width: '100%', m: 0 }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </RadioGroup>
                  </FormControl>
                ) : (
                  <Typography sx={{ color: '#ffa726', textAlign: 'center', p: 2 }}>
                    No products available
                  </Typography>
                )}
              </Box>

              {selectedProduct && (
                <Box
                  sx={{
                    border: '1px solid #333',
                    borderRadius: '8px',
                    p: 2,
                    backgroundColor: '#222',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                      {selectedProduct.name}
                    </Typography>
                    <Typography sx={{ color: '#E31E25', fontWeight: 700 }}>
                      ₹{selectedProduct.price.toLocaleString()}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      type="number"
                      size="small"
                      label="Quantity"
                      value={selectedProduct.quantity}
                      onChange={(e) =>
                        setSelectedProduct({
                          ...selectedProduct,
                          quantity: Math.max(1, parseInt(e.target.value) || 1),
                        })
                      }
                      sx={{ width: '120px', ...purchaseFieldSx }}
                      inputProps={{ min: 1 }}
                    />
                    <Typography sx={{ color: '#fff' }}>
                      × {selectedProduct.quantity} ={' '}
                      <Box component="span" sx={{ color: '#0BE600', fontWeight: 700 }}>
                        ₹{(selectedProduct.price * selectedProduct.quantity).toLocaleString()}
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Box>
              <Typography gutterBottom sx={{ color: '#fff', fontWeight: 600 }}>
                Remarks (Optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter any additional notes or remarks for this lead..."
                value={leadForm.remarks}
                onChange={(e) =>
                  setLeadForm({ ...leadForm, remarks: e.target.value })
                }
                sx={purchaseFieldSx}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={purchaseDialogActionsSx}>
          <SecondaryButton title="Cancel" onClick={onClose} disabled={loading} />
          {loading ? (
            <CircularProgress size={24} sx={{ color: '#E31E25' }} />
          ) : (
            <PrimaryButton
              title="Create Lead"
              onClick={handleSubmit}
              disabled={!leadForm.userId || !leadForm.productId}
            />
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CreateLeadDialog;
